import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { Alert, AlertDescription } from "@/shared/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";
import { AlertCircle, Eye } from "lucide-react";
import { getRequiredFields, OPTIONAL_FIELDS, type ImportMode } from "./fields";
import type { ImportError, ParsedAsset } from "./validation";

interface Property {
  id: string;
  name: string;
  address: string;
}

interface MappingStepProps {
  propertyId?: string;
  properties: Property[];
  importMode: ImportMode;
  selectedProperty: string;
  setSelectedProperty: (id: string) => void;
  file: File | null;
  parsedData: ParsedAsset[];
  columnMapping: Record<string, string>;
  setColumnMapping: (updater: (prev: Record<string, string>) => Record<string, string>) => void;
  errors: ImportError[];
  importing: boolean;
  onValidate: () => void;
  onImport: () => void;
}

export function MappingStep({
  propertyId,
  properties,
  importMode,
  selectedProperty,
  setSelectedProperty,
  file,
  parsedData,
  columnMapping,
  setColumnMapping,
  errors,
  importing,
  onValidate,
  onImport,
}: MappingStepProps) {
  const requiredFields = getRequiredFields(importMode);
  const allFields = [...requiredFields, ...OPTIONAL_FIELDS];

  return (
    <>
      {importMode === "single" && (
        <Card>
          <CardHeader>
            <CardTitle>Property Selection</CardTitle>
          </CardHeader>
          <CardContent>
            {!propertyId && (
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger>
                  <SelectValue placeholder="Select property for assets" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((property) => (
                    <SelectItem key={property.id} value={property.id}>
                      {property.name} - {property.address}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {propertyId && (
              <p className="text-sm text-muted-foreground">
                Importing into: <span className="font-medium text-foreground">
                  {properties.find((p) => p.id === propertyId)?.name ?? propertyId}
                </span>
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {importMode === "multi" && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            In multi-property mode, assets will be assigned to properties based on the property_id column in your CSV file.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Column Mapping</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {allFields.map((field) => (
              <div key={field.key} className="space-y-2">
                <label className="text-sm font-medium">
                  {field.label}
                  {requiredFields.includes(field) && (
                    <span className="text-destructive ml-1">*</span>
                  )}
                </label>
                <Select
                  value={columnMapping[field.key] || "none"}
                  onValueChange={(value) => {
                    setColumnMapping((prev) => ({
                      ...prev,
                      [field.key]: value === "none" ? "" : value,
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select column..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- None --</SelectItem>
                    {file && parsedData.length > 0 &&
                      Object.keys(parsedData[0]).map((column) => (
                        <SelectItem key={column} value={column}>
                          {column}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <Button onClick={onValidate} variant="outline">
              <Eye className="h-4 w-4 mr-2" />
              Validate Data
            </Button>
            <Button onClick={onImport} disabled={importing || (importMode === "single" && !selectedProperty)}>
              {importing ? "Importing..." : "Import Assets"}
            </Button>
          </div>

          {errors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Found {errors.length} validation errors. Please fix them before importing.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {parsedData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Data Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {Object.keys(parsedData[0]).slice(0, 10).map((column) => (
                      <TableHead key={column}>{column}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedData.slice(0, 5).map((row, index) => (
                    <TableRow key={index}>
                      {Object.keys(parsedData[0]).slice(0, 10).map((column) => (
                        <TableCell key={column} className="max-w-32 truncate">
                          {row[column]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {errors.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-destructive">Validation Errors</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {errors.map((error, index) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  <Badge variant="destructive">Row {error.row}</Badge>
                  <span className="font-medium">{error.field}:</span>
                  <span>{error.message}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}
